/**
 * ProfileContextStrip — „Отчетено от профила".
 * Всеки модул показва какво знае профилът за пациента: декларирани заболявания,
 * лекарства и бележка, когато лекарство обяснява показателя.
 * Данни: GET /api/profile-signals?domain=...
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { UserCheck, AlertTriangle, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileContextStrip({ domain = "general", isDark = false, className = "" }) {
  const [d, setD] = useState(null);

  useEffect(() => {
    let live = true;
    axios.get(`${API}/profile-signals`, { params: { domain } })
      .then((r) => { if (live) setD(r.data); })
      .catch(() => { if (live) setD(null); });
    return () => { live = false; };
  }, [domain]);

  if (!d) return null;
  const empty = !d.conditions?.length && !d.medications?.length;

  return (
    <div className={`rounded-xl border p-3 ${className} ${
      isDark ? "bg-white/[0.04] border-white/10" : "bg-slate-50 border-slate-200"}`}
      data-testid={`profile-context-${domain}`}>
      <div className="flex items-start gap-2">
        <UserCheck className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {empty ? (
            <div className="text-[11px] opacity-80" data-testid="profile-context-empty">
              {d.empty_bg}
            </div>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase opacity-60">
                Отчетено от профила
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {d.conditions.slice(0, 6).map((c) => (
                  <span key={c.id}
                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      c.relevant ? "bg-emerald-500/15 text-emerald-600"
                                 : (isDark ? "bg-white/10" : "bg-white border border-slate-200")}`}
                    data-testid={`profile-context-cond-${c.id}`}>
                    {c.label_bg}
                  </span>
                ))}
                {d.medications.slice(0, 5).map((m, i) => (
                  <span key={i}
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold
                               bg-violet-500/15 text-violet-500"
                    data-testid="profile-context-med">
                    {m.name}
                  </span>
                ))}
              </div>
            </>
          )}
          {!!d.marker_notes?.length && (
            <div className="mt-2 space-y-1" data-testid="profile-context-notes">
              {d.marker_notes.map((n, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[11px]">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                  <span>
                    <span className="font-semibold">{n.affected_bg}:</span> {n.note_bg}
                    <span className="opacity-60"> ({n.med_bg})</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <Link to={d.profile_route}
            className="inline-flex items-center gap-0.5 text-[10px] font-bold mt-1.5
                       text-cyan-500 hover:underline"
            data-testid="profile-context-link">
            Моите данни <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}
